// Localizacao.js
// (Self-contained with injected CSS)

import React, { useState, useEffect, useRef } from 'react'; // Added useRef
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet'; // Added useMap
import { FaCar, FaSearch, FaMapMarkerAlt, FaListAlt, FaKey, FaSlash } from 'react-icons/fa';
import 'leaflet/dist/leaflet.css';

// NOTE: Default Leaflet icons are usually handled fine by react-leaflet,
// but if you specifically had issues, keep the workaround.
// The DivIcon below will override the default marker anyway.
/*
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });
*/

// --- Constants ---
const onlineColor = '#2ECC71';
const offlineColor = '#E74C3C';
const markerIconColor = '#A71C1C'; // Red color for marker icon/tooltip border
const componentStyleId = 'localizacao-styles'; // ID for the injected style tag

// --- CSS to be Injected ---
// (Contains styles for custom markers and tooltips)
const injectedCss = `
  /* Style for the container div Leaflet creates for the custom icon */
  .custom-marker-icon {
    /* No background/border needed here, it's handled by the inner div */
  }

  /* Style for the red circle background inside the custom icon HTML */
  .custom-marker-icon .marker-icon-background {
    background-color: ${markerIconColor}; /* Red color */
    border-radius: 50%; /* Make it a circle */
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #555; /* Optional: dark border */
    box-shadow: 1px 1px 3px rgba(0,0,0,0.4); /* Optional: subtle shadow */
    /* Center the background div relative to the iconAnchor */
    margin-left: -15px;
    margin-top: -15px;
  }

  /* Style for the FaCar icon inside the circle */
  .custom-marker-icon .marker-icon-background svg {
    font-size: 16px; /* Adjust car icon size */
    color: white; /* Ensure it's white */
  }

  /* Style for the permanent tooltip (license plate label) */
  .leaflet-tooltip.plate-tooltip {
    background-color: white;
    border: 2px solid ${markerIconColor}; /* Use variable for Red border */
    border-radius: 8px; /* Rounded corners */
    padding: 2px 6px !important; /* Adjust padding */
    color: #333; /* Text color */
    font-weight: bold;
    font-size: 11px; /* Adjust font size */
    white-space: nowrap; /* Prevent wrapping */
    box-shadow: none; /* Remove default tooltip shadow */
    /* Position adjustment */
    margin-top: 0 !important; /* Override default margin */
  }
  .leaflet-tooltip-top.plate-tooltip::before {
     /* Hides the default pointer */
     display: none;
     /* Or style it if you keep it: */
     /* border-top-color: ${markerIconColor} !important; */
  }

  /* Optional: Improve tile rendering on high-res displays */
  .leaflet-tile {
   image-rendering: -webkit-optimize-contrast;
   image-rendering: crisp-edges;
   image-rendering: pixelated;
  }
`;

// --- Helper Component to Handle Map Events (like flyTo) ---
// We need this because useMap() can only be called inside a MapContainer child
function MapController({ center, zoom }) {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.flyTo(center, zoom || map.getZoom(), {
                animate: true,
                duration: 1.0 // Adjust animation duration
            });
        }
    }, [center, zoom, map]); // Re-run effect if center or zoom changes

    return null; // This component doesn't render anything visible
}


// --- Main Component ---
export default function Localizacao() {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mapTarget, setMapTarget] = useState({ center: [-19.9326, -43.9119], zoom: 12 }); // Initial state for map center/zoom
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRowId, setExpandedRowId] = useState(null);
    const navigate = useNavigate(); // Keep if used for navigation elsewhere

    // Effect to Inject and Remove CSS
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.id = componentStyleId;
        styleElement.innerHTML = injectedCss;
        document.head.appendChild(styleElement);

        // Cleanup function to remove the style when the component unmounts
        return () => {
            const existingStyleElement = document.getElementById(componentStyleId);
            if (existingStyleElement) {
                document.head.removeChild(existingStyleElement);
            }
        };
    }, []); // Empty dependency array ensures this runs only once on mount/unmount

    // Effect for Data Fetching
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("token"); // Recupera o token do localStorage
                const response = await axios.get('https://sis.itraker.com.br/api/associacaoapp', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });                const dados = response.data.dados || [];
                const veiculos = dados.map(item => ({
                    id: item.idProdutos,
                    marca: item.veiculo_produto?.split(' ')[0] || 'N/I',
                    modelo: item.veiculo_produto?.replace(item.veiculo_produto?.split(' ')[0], '').trim() || 'N/I',
                    placa: item.placa_produto || 'N/P',
                    latitude: item.lat ? parseFloat(item.lat) : null,
                    longitude: item.long ? parseFloat(item.long) : null,
                    status: item.statusassociacao === 'ATIVO' ? 'online' : 'offline',
                    ignicao: item.statusassociacao === 'ATIVO',
                    cor: item.cor_veiculo_produto || '-',
                    ano: item.data_produto?.split('-')[0] || '-',
                    velocidade: 0, // Default, update if available from API
                    cliente: item.cliente_produto || '-',
                    motorista: 'Sem motorista', // Default, update if available
                    contato: item.notificacao_whatsapp || '-'
                }));
                setVehicles(veiculos);

                // Optional: Set initial map center based on the first vehicle with coordinates
                // const firstVehicleWithCoords = veiculos.find(v => v.latitude && v.longitude);
                // if (firstVehicleWithCoords) {
                //     setMapTarget({ center: [firstVehicleWithCoords.latitude, firstVehicleWithCoords.longitude], zoom: 13 });
                // }

            } catch (err) {
                console.error("API Fetch Error:", err);
                setError('Erro ao carregar os dados da API. Verifique o console.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []); // Removed navigate dependency, assuming fetch is only on mount

    const filteredVehicles = vehicles.filter(v =>
        v.placa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.modelo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.marca?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleRowExpansion = (id) => {
        setExpandedRowId(prev => prev === id ? null : id);
    };

    const handleCenterMap = (lat, lng) => {
        if (lat && lng) {
            setMapTarget({ center: [lat, lng], zoom: 16 }); // Update target state, MapController will handle the rest
        }
    };

    const vehiclesWithCoords = vehicles.filter(v =>
        typeof v.latitude === 'number' && !isNaN(v.latitude) &&
        typeof v.longitude === 'number' && !isNaN(v.longitude)
    );

    // --- Render Logic ---
    if (loading) return <div style={styles.centeredMessage}>Carregando mapa e veículos...</div>;
    if (error) return <div style={{ ...styles.centeredMessage, ...styles.errorMessage }}>Erro: {error}</div>;

    return (
        <div style={styles.pageContainer}>
            {/* Map Section */}
            <div style={styles.mapWrapper}>
                <MapContainer
                    center={mapTarget.center} // Controlled center
                    zoom={mapTarget.zoom} // Controlled zoom
                    scrollWheelZoom={true}
                    style={styles.mapContainer}
                    whenCreated={mapInstance => { /* Can store map instance if needed, but useMap is often better */ }}
                >
                    <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {/* Map Controller to handle state changes */}
                    <MapController center={mapTarget.center} zoom={mapTarget.zoom} />

                    {/* Render Vehicle Markers */}
                    {vehiclesWithCoords.map(vehicle => {
                        const carIconHtml = ReactDOMServer.renderToString(
                            <div className="marker-icon-background">
                                <FaCar style={{ color: 'white' }}/>
                            </div>
                        );
                        const customMapIcon = L.divIcon({
                            html: carIconHtml,
                            className: 'custom-marker-icon',
                            iconSize: [30, 30],
                            iconAnchor: [15, 30], // Bottom center
                            popupAnchor: [0, -30],
                            tooltipAnchor: [0, -35] // Adjust tooltip anchor slightly higher than icon bottom
                        });

                        return (
                            <Marker
                                key={vehicle.id}
                                position={[vehicle.latitude, vehicle.longitude]}
                                icon={customMapIcon}
                            >
                                <Tooltip
                                    direction="top"
                                    offset={[0, 0]} // Offset relative to tooltipAnchor
                                    opacity={1}
                                    permanent
                                    className="plate-tooltip"
                                >
                                    {vehicle.placa?.toUpperCase()}
                                </Tooltip>
                                <Popup>
                                    <b>Placa:</b> {vehicle.placa}<br />
                                    <b>Status:</b> {vehicle.status === 'online' ? 'Ligado' : 'Desligado'}<br />
                                    <b>Marca:</b> {vehicle.marca}<br />
                                    <b>Modelo:</b> {vehicle.modelo}<br />
                                    <b>Cor:</b> {vehicle.cor}<br/>
                                    <b>Ano:</b> {vehicle.ano}
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>

            {/* Search Input */}
            <div style={{ padding: '0 10px 10px 10px' }}>
                <input
                    type="text"
                    placeholder="Buscar por placa, modelo ou marca..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            {/* Table Section */}
            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                    <tr>
                        <th style={styles.th}>Ign</th>
                        <th style={styles.th}>Placa</th>
                        <th style={styles.th}>Marca</th>
                        <th style={styles.th}>Modelo</th>
                        <th style={styles.th}>Cor</th>
                        <th style={styles.th}>Ano</th>
                        <th style={styles.th}>km/h</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredVehicles.length > 0 ? filteredVehicles.map((vehicle) => (
                        <React.Fragment key={vehicle.id}>
                            <tr onClick={() => toggleRowExpansion(vehicle.id)} style={styles.tableRow}>
                                <td style={styles.td}>
                                    {vehicle.ignicao ? (
                                        <FaKey style={{ ...styles.iconBase, color: onlineColor }} title="Ignição Ligada" />
                                    ) : (
                                        <FaKey style={{ ...styles.iconBase, color: offlineColor, opacity: 0.7 }} title="Ignição Desligada"/>
                                    )}
                                </td>
                                <td style={{ ...styles.td, ...styles.tdPlaca }}>{vehicle.placa?.toUpperCase()}</td>
                                <td style={styles.td}>{vehicle.marca}</td>
                                <td style={styles.td}>{vehicle.modelo}</td>
                                <td style={styles.td}>{vehicle.cor}</td>
                                <td style={styles.td}>{vehicle.ano}</td>
                                <td style={styles.td}>{vehicle.velocidade}</td>
                            </tr>
                            {expandedRowId === vehicle.id && (
                                <tr style={styles.expandedRow}>
                                    <td colSpan="7" style={styles.expandedCell}>
                                        <div style={styles.expandedContent}>
                                            <div><b>Localização:</b> {vehicle.latitude ? `${vehicle.latitude}, ${vehicle.longitude}` : 'Indisponível'}</div>
                                            <div><b>Cliente:</b> {vehicle.cliente}</div>
                                            <div><b>Contato:</b> {vehicle.contato}</div>
                                            <div><b>Motorista:</b> {vehicle.motorista}</div>
                                            <div style={styles.optionsContainer}>
                                                <b>Opções:</b>
                                                <FaCar style={{...iconStyle, cursor:'pointer'}} title="Detalhes Veículo" />
                                                <FaMapMarkerAlt
                                                    style={{...iconStyle, cursor:'pointer'}}
                                                    title="Centralizar no Mapa"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent row toggle when clicking icon
                                                        handleCenterMap(vehicle.latitude, vehicle.longitude);
                                                    }}
                                                />
                                                <FaListAlt style={{...iconStyle, cursor:'pointer'}} title="Histórico" />
                                                <FaSlash style={{...iconStyle, cursor:'pointer'}} title="Bloquear" />
                                                <FaKey style={{...iconStyle, cursor:'pointer'}} title="Comandos" />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    )) : (
                        <tr>
                            <td colSpan="7" style={styles.noResults}>Nenhum veículo encontrado para a busca.</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// --- Component Inline Styles (using JS object) ---
// These styles don't require complex selectors or pseudo-elements
// The marker/tooltip styles are handled by the injected CSS
const styles = {
    pageContainer: { padding: '15px', fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif', display: 'flex', flexDirection: 'column', gap: '15px', backgroundColor: '#f0f2f5', minHeight: '100vh' },
    centeredMessage: { display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, fontSize: '16px', color: '#555', textAlign: 'center', padding: '30px' },
    errorMessage: { color: '#D32F2F', backgroundColor: '#FFEBEE', padding: '15px', borderRadius: '8px', border: '1px solid #FFCDD2', maxWidth: '600px', margin: '20px auto' },
    mapWrapper: {
        height: '45vh', // Adjusted height
        minHeight: '350px',
        width: '100%',
        border: '1px solid #ccc',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    },
    mapContainer: { height: "100%", width: "100%" },
    searchInput: {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '5px',
        border: '1px solid #ccc',
        fontSize: '14px',
        boxSizing: 'border-box',
    },
    tableContainer: {
        width: '100%',
        overflowX: 'auto',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        padding: '12px 10px',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: '600',
        color: '#495057',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    td: {
        borderBottom: '1px solid #eee',
        padding: '10px 10px',
        fontSize: '14px',
        color: '#333',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    },
    tdPlaca: { fontWeight: '500', color: '#0056b3' },
    iconBase: { fontSize: '16px', verticalAlign: 'middle' },
    tableRow: { cursor: 'pointer', transition: 'background-color 0.2s ease' },
    expandedRow: { backgroundColor: '#fafafa' },
    expandedCell: { padding: '15px 20px', borderBottom: '1px solid #ddd'},
    expandedContent: { display: 'flex', flexWrap: 'wrap', gap: '15px 25px', fontSize: '13px', color: '#444' },
    optionsContainer: { display: 'flex', alignItems: 'center', gap: '15px', flexBasis: '100%', marginTop: '10px' }, // Add gap and margin
    noResults: { textAlign: 'center', padding: '20px', color: '#777', fontStyle: 'italic' },
};

// Base style for clickable icons in expanded row (merged with specific styles)
const iconStyle = { fontSize: '18px', color: markerIconColor }; // Adjusted size/color